import Vue from 'vue'

import applogger from '@applogger'

const gqlDoc = `
  query seo ($path: String!) {
    seo(path: $path) {
      path
      title
      description
      canonical
      xmlsitemap {
        include
        changefreq
        priority
      }
      robots {
        unavailable_after
        flags {
          index
          follow
          imageindex
          archive
          snippet
          translate
        }
      }
      created
      updated
    }
  }

  mutation seoInput ($path: String!, $oldpath: String, $settings: SEOSettingsInput!) {
    seo(path: $path, oldpath: $oldpath, seosettings: $settings) {
      path
      title
      description
      canonical
      xmlsitemap {
        include
        changefreq
        priority
      }
      robots {
        unavailable_after
        flags {
          index
          follow
          imageindex
          archive
          snippet
          translate
        }
      }
      created
      updated
    }
  }
`

const gqlItemDoc = `
  mutation updatePath($path: String!, $oldpath: String!) {
    updatePath(path: $path, oldpath: $oldpath)
  }
`
const defaults = {
  title: '',
  path: '',
  oldpath: '',
  description: '',
  robots: {
    flags: {
      index: true,
      follow: true,
      imageindex: true,
      archive: true,
      snippet: true,
      translate: true
    },
    unavailable_after: ''
  },
  canonical: '',
  xmlsitemap: {
    include: true,
    changefreq: 'monthly',
    priority: 0.5
  }
}

const stripLeadingSlash = path => path.charAt(0) === '/' ? path.substr(1) : path
// const getValue = (obj, key, def) => obj[key] ? obj[key] : def

const state = () => ({
  cache: {},
  defaults: JSON.parse(JSON.stringify(defaults))
})

const actions = {
  load: function({ commit, state, rootGetters }, payload) {
    if (payload.isNew) {
      commit('STORE_SEO_SETTINGS', { path: payload.path, ...defaults })
      return JSON.parse(JSON.stringify(defaults))
    }

    payload.path = stripLeadingSlash(payload.path)

    if (state.cache[payload.path] === undefined) {
      let gqlClient = rootGetters['getGraphQLClient']('thesaurum', gqlDoc)
      return gqlClient.query({opName: 'seo', variables: {path: payload.path}})
        .then(response => {
          if (response.errors) {
            // Don't know what to do, return an empty dataset to avoid errors elsewhere...
            // TODO: add a report error to indicate that the existing SEO Settings were not correct loaded.
            // NOTE: This will also trigger when attempting to get SEO data for edit pages and the like.
            commit('STORE_SEO_SETTINGS', { ...defaults, path: payload.path })
            return JSON.parse(JSON.stringify(defaults))
          }

          response.data.seo.xmlsitemap.priority = parseFloat(response.data.seo.xmlsitemap.priority)
          response.data.seo.oldpath = response.data.seo.path

          commit('STORE_SEO_SETTINGS', { ...response.data.seo, path: payload.path })
          return state.cache[payload.path].data
        })
        .catch(_err => {
          commit('STORE_SEO_SETTINGS', { ...defaults, path: payload.path })
          return JSON.parse(JSON.stringify(defaults))
        })
    }
    else {
      return state.cache[payload.path].data
    }
  },
  save: function({commit, getters, rootGetters}, payload) {
    commit('CLEAN_CACHE')

    if (payload.isNew) {
      return Promise.resolve(JSON.parse(JSON.stringify(defaults)))
    }

    let seo = JSON.parse(JSON.stringify(payload))

    const path = seo.path
    const oldpath = seo.oldpath

    let itemUpdate = Promise.resolve(true)
    if (!seo.itemUpdated) {
      // We need to update the backend path ourselves.
      let gqlItemClient = rootGetters['getGraphQLClient'](null, gqlItemDoc)
      itemUpdate = gqlItemClient.query({opName: 'updatePath', variables: {path: path, oldpath: oldpath}})
        .then(response => {
          if (response.errors) {
            throw new Error(response.errors.map(e => e.message).join('\n'))
          }
        })
        .catch(err => {
          applogger.error(err)
          // TODO: Add a report error here to indicate that the seo settings did not get updated
          return Promise.reject(err)
        })
    }

    delete seo.path
    delete seo.oldpath
    delete seo.created
    delete seo.updated

    seo.xmlsitemap.priority = seo.xmlsitemap.priority.toFixed(1)
    let gqlClient = rootGetters['getGraphQLClient']('thesaurum', gqlDoc)

    return Promise.all([
      itemUpdate,
      gqlClient.query({opName: 'seoInput', variables: {path: path, oldpath: oldpath, settings: seo}})
        .then(response => {
          if (response.errors) {
            throw new Error(response.errors.map(e => e.message).join('\n'))
          }
          response.data.seo.xmlsitemap.priority = parseFloat(response.data.seo.xmlsitemap.priority)
          response.data.seo.oldpath = response.data.seo.path
          commit('STORE_SEO_SETTINGS', response.data.seo)
        })
        .catch(err => {
          // TODO: Add report error here to indicate that the SEO Settings were no updated.)
          applogger.error(err)
        })
    ])
  }
}

const mutations = {
  STORE_SEO_SETTINGS: function(state, payload) {
    const data = {
      data: payload,
      expires: new Date().getTime() + 300
    }
    Vue.set(state.cache, payload.path, data)
  },
  SEO_CACHE_SET: function(state, payload) {
    Vue.set(state.cache, payload.key, payload.value)
  },
  CLEAN_CACHE: function(state) {
    Object.keys(state.cache).forEach(key => state.cache[key].expires <= new Date().getTime() && delete state.cache[key])
  }
}

const getters = {
  getSeoData: function(state) {
    return (path) => {
      path = stripLeadingSlash(path)
      if (state.cache[path] !== undefined) {
        return state.cache[path].data
      }
      return JSON.parse(JSON.stringify(defaults))
    }
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
